import { useCallback, useEffect, useState } from "react";

import type { DataTableDensity } from "~/v3-poc/components/DataTable";

export type OrdersTableDensity = DataTableDensity;

export interface OrdersTableDisplay {
  density: OrdersTableDensity;
  horizontalLines: boolean;
  verticalLines: boolean;
}

export const ORDERS_TABLE_DISPLAY_STORAGE_KEY = "atd:orders:table-display:v1";

const DEFAULT_DISPLAY: OrdersTableDisplay = {
  density: "normal",
  horizontalLines: true,
  verticalLines: false,
};

export interface OrdersTableDisplayStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

const getStorage = (): OrdersTableDisplayStorage | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const isDensity = (value: unknown): value is OrdersTableDensity =>
  value === "condensed" || value === "normal" || value === "expanded";

export const sanitizeOrdersTableDisplay = (
  value: unknown,
): OrdersTableDisplay => {
  if (typeof value !== "object" || value === null) {
    return DEFAULT_DISPLAY;
  }
  const record = value as Record<string, unknown>;
  return {
    density: isDensity(record.density)
      ? record.density
      : DEFAULT_DISPLAY.density,
    horizontalLines:
      typeof record.horizontalLines === "boolean"
        ? record.horizontalLines
        : DEFAULT_DISPLAY.horizontalLines,
    verticalLines:
      typeof record.verticalLines === "boolean"
        ? record.verticalLines
        : DEFAULT_DISPLAY.verticalLines,
  };
};

export const loadOrdersTableDisplay = (
  storage: OrdersTableDisplayStorage | null = getStorage(),
): OrdersTableDisplay => {
  if (!storage) {
    return DEFAULT_DISPLAY;
  }
  try {
    const raw = storage.getItem(ORDERS_TABLE_DISPLAY_STORAGE_KEY);
    return raw === null
      ? DEFAULT_DISPLAY
      : sanitizeOrdersTableDisplay(JSON.parse(raw));
  } catch {
    return DEFAULT_DISPLAY;
  }
};

export const persistOrdersTableDisplay = (
  display: OrdersTableDisplay,
  storage: OrdersTableDisplayStorage | null = getStorage(),
): boolean => {
  if (!storage) {
    return false;
  }
  try {
    storage.setItem(
      ORDERS_TABLE_DISPLAY_STORAGE_KEY,
      JSON.stringify(sanitizeOrdersTableDisplay(display)),
    );
    return true;
  } catch {
    return false;
  }
};

export const useOrdersTableDisplay = (
  storage: OrdersTableDisplayStorage | null = getStorage(),
) => {
  const [display, setDisplay] = useState<OrdersTableDisplay>(() =>
    loadOrdersTableDisplay(storage),
  );

  useEffect(() => {
    persistOrdersTableDisplay(display, storage);
  }, [display, storage]);

  const setDensity = useCallback((density: OrdersTableDensity) => {
    setDisplay((current) => ({ ...current, density }));
  }, []);

  const setHorizontalLines = useCallback((horizontalLines: boolean) => {
    setDisplay((current) => ({ ...current, horizontalLines }));
  }, []);

  const setVerticalLines = useCallback((verticalLines: boolean) => {
    setDisplay((current) => ({ ...current, verticalLines }));
  }, []);

  return {
    ...display,
    setDensity,
    setHorizontalLines,
    setVerticalLines,
  };
};
